import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '../types';
import type { ChatTarget } from '../types';
import { fetchOlderMessages } from '../fetchOlderMessages';
import { fetchMessagesBackTo } from '../fetchMessagesBackTo';
import { mergeMessages } from '../cache/messageCache';
import { chatKey } from '../chatTarget';

type Params = {
  target: ChatTarget;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  loading: boolean;
  signedUrls: Record<string, string>;
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>;
};

/**
 * How many messages a jump will load to reach its target before giving up.
 *
 * This is a DOM budget, not a network one: the message list has no virtualization, so
 * every loaded message is a real node. Lifting it means windowing the list first.
 */
const MAX_REVEAL_MESSAGES = 600;
/** Messages loaded beyond the target, so it does not land flush against the top. */
const REVEAL_CONTEXT_BUFFER = 20;

export function useChatScroll({
  target,
  scrollContainerRef,
  messages,
  setMessages,
  loading,
  signedUrls,
  setHasMore,
}: Params) {
  const key = chatKey(target);

  const [loadingMore, setLoadingMore] = useState(false);

  const skipAutoScrollRef = useRef(false);
  // When true, all scrollToBottom calls are no-ops (used during "load older" flow).
  const suppressScrollRef = useRef(false);

  useEffect(() => {
    setLoadingMore(false);
    skipAutoScrollRef.current = false;
    suppressScrollRef.current = false;
  }, [key]);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    if (suppressScrollRef.current) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  // Scroll to bottom on initial load (loading → false), instant so we land
  // before signed-URL fetches / image loads shift the layout.
  useEffect(() => {
    if (!loading) {
      scrollToBottom('instant');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Re-scroll when signed URLs resolve — images just became visible and may
  // have expanded the layout. suppressScrollRef blocks this during "load older".
  useEffect(() => {
    if (loading) return;
    scrollToBottom('instant');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedUrls, loading]);

  // Keep the bottom pinned when the CONTAINER resizes, not just when its contents change.
  //
  // The pinned-message banner is the reason this exists: it is a sibling above the
  // message list that mounts once the thread's pins load, a beat after the messages have
  // already painted. That shrinks the scroller by its own height while scrollTop stays
  // put, so the newest message slides up under the composer. The same thing happens when
  // the banner goes away, and when the soft keyboard resizes the viewport.
  //
  // Only re-pins when the user was already near the bottom -- someone reading back
  // through history must not be yanked forward by a banner appearing.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const NEAR_BOTTOM_PX = 40;
    let wasNearBottom = true;

    const trackPosition = () => {
      wasNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    };
    trackPosition();
    el.addEventListener('scroll', trackPosition, { passive: true });

    const observer = new ResizeObserver(() => {
      if (!wasNearBottom) return;
      scrollToBottom('instant');
    });
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', trackPosition);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Scroll to bottom when new messages arrive (real-time inserts).
  useEffect(() => {
    if (loading) return;
    if (skipAutoScrollRef.current) {
      // Skip auto-scroll once (used when prepending older messages)
      skipAutoScrollRef.current = false;
      return;
    }
    scrollToBottom('smooth');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, loading]);

  /**
   * Bring one message into view, loading back to it if it is not on screen.
   *
   * A pinned message is usually older than the newest page the thread opens with, so more
   * often than not it is not in the DOM at all when someone taps it in the pin list.
   *
   * fetchMessagesBackTo does this in ONE round trip: it counts how far back the message is
   * (a head-only count, no rows read) and then fetches exactly that far. The earlier
   * version walked backwards a page at a time, which meant up to a dozen sequential
   * requests to reach a timestamp we already knew.
   *
   * It also cannot loop on stale state, which the paging version had to be careful about:
   * loadOlderMessages guards on `loadingMore` and closes over `messages`, both a render
   * behind inside a loop.
   */
  const revealMessage = async (
    messageId: string,
    createdAt?: string | null
  ): Promise<boolean> => {
    const elementId = `chat-message-${messageId}`;

    const scrollToElement = () => {
      const el = document.getElementById(elementId);
      if (!el) return false;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return true;
    };

    // Already in the DOM -- nothing to load.
    if (scrollToElement()) return true;
    if (loading || !createdAt) return false;

    // Every auto-scroll-to-bottom is wrong for the whole of this: we are deliberately
    // going somewhere that is not the bottom.
    suppressScrollRef.current = true;
    skipAutoScrollRef.current = true;
    setLoadingMore(true);

    try {
      const result = await fetchMessagesBackTo({
        target,
        createdAt,
        maxMessages: MAX_REVEAL_MESSAGES,
        contextBuffer: REVEAL_CONTEXT_BUFFER,
      });

      if (result.status === 'too-far') {
        suppressScrollRef.current = false;
        return false;
      }

      // Merge rather than replace. The fetch is contiguous from the newest message, so it
      // is a superset of what is loaded -- but a realtime insert may have landed while it
      // was in flight, and that message is newer than everything here. mergeMessages
      // dedupes and sorts, and the result stays contiguous either way.
      setMessages((prev) => mergeMessages(prev, result.messages));
      if (result.reachedStart) setHasMore(false);

      // Two frames: one for React to render the prepended batch, one for layout to settle
      // before we measure where the message ended up.
      return await new Promise<boolean>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const ok = scrollToElement();
            suppressScrollRef.current = false;
            resolve(ok);
          });
        });
      });
    } catch (err) {
      console.error('[EventChat] Failed to reveal message', err);
      suppressScrollRef.current = false;
      return false;
    } finally {
      setLoadingMore(false);
    }
  };

  const loadOlderMessages = async () => {
    if (loadingMore || loading) return;
    if (!messages.length) return;

    // Suppress all auto-scrolls while we prepend older messages.
    suppressScrollRef.current = true;
    skipAutoScrollRef.current = true;

    const container = scrollContainerRef.current;
    const scrollHeightBefore = container?.scrollHeight ?? 0;
    const scrollTopBefore = container?.scrollTop ?? 0;

    setLoadingMore(true);

    try {
      const oldest = messages[0].created_at;
      const older = await fetchOlderMessages({
        target,
        oldestCreatedAt: oldest,
      });

      if (!older.length) {
        setHasMore(false);
        suppressScrollRef.current = false;
        return;
      }

      setMessages((prev) => [...older, ...prev]);

      // After React renders the prepended messages, anchor the viewport so the
      // user stays on the same message they were reading.
      requestAnimationFrame(() => {
        if (container) {
          const added = container.scrollHeight - scrollHeightBefore;
          container.scrollTop = scrollTopBefore + added;
        }
        // Re-enable auto-scroll only after the anchor has been applied and any
        // signed-URL / onLoad effects for the older batch have had a chance to run.
        requestAnimationFrame(() => {
          suppressScrollRef.current = false;
        });
      });
    } catch (err) {
      console.error('[EventChat] Failed to load older messages', err);
      suppressScrollRef.current = false;
    } finally {
      setLoadingMore(false);
    }
  };

  return {
    loadingMore,
    loadOlderMessages,
    scrollToBottom,
    revealMessage,
  };
}

